import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllTickets } from '../../store/slices/ticketSlice';
import { useParams, useSearchParams } from 'react-router-dom';
import { Ticket, Calendar, Users, Search, QrCode } from 'lucide-react';
import ScannerModal from '../modals/ScannerModal';

const AdminTickets = () => {
  const dispatch = useDispatch();
  const { tickets, loading, error } = useSelector((state) => state.tickets);
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventIdParam = searchParams.get('eventId');
  const eventTitleParam = searchParams.get('eventTitle');
  const [activeEventId, setActiveEventId] = useState(eventIdParam || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      dispatch(fetchAllTickets({ slug }));
    }
  }, [dispatch, slug]);

  useEffect(() => {
    setActiveEventId(eventIdParam || 'all');
  }, [eventIdParam]);

  const handleScanSuccess = () => {
    // Recargar tickets para ver el cambio de estado
    if (slug) {
      dispatch(fetchAllTickets({ slug }));
    }
  };

  // Agrupar tickets por evento
  const groupedData = useMemo(() => {
    if (!Array.isArray(tickets)) return { all: [], events: {} };

    const events = {};
    const filtered = tickets.filter(t =>
      (t.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.event?.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.forEach(ticket => {
      const eventId = ticket.event?._id || 'unknown';
      if (!events[eventId]) {
        events[eventId] = {
          title: ticket.event?.title || 'Sin Evento',
          tickets: [],
          stats: { total: 0, valid: 0, used: 0 }
        };
      }
      events[eventId].tickets.push(ticket);
      events[eventId].stats.total++;
      if (ticket.valid) events[eventId].stats.valid++;
      else if (ticket.status === 'expired') events[eventId].stats.expired = (events[eventId].stats.expired || 0) + 1;
      else events[eventId].stats.used++;
    });

    return { all: filtered, events };
  }, [tickets, searchTerm]);

  const eventIds = Object.keys(groupedData.events);

  // No retornamos aquí para no desmontar el ScannerModal
  // if (loading) { ... }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-500/10 border border-red-500/20 p-6 rounded-3xl text-center">
          <p className="text-red-400 font-bold mb-2">Error al cargar tickets</p>
          <p className="text-red-400/60 text-sm">{typeof error === 'object' ? JSON.stringify(error) : error}</p>
        </div>
      </div>
    );
  }

  const currentTickets = activeEventId === 'all'
    ? groupedData.all
    : (groupedData.events[activeEventId]?.tickets || []);

  const selectedEventTitle = activeEventId === 'all'
    ? null
    : groupedData.events[activeEventId]?.title || eventTitleParam;

  const selectEventFilter = (eventId, eventTitle) => {
    setActiveEventId(eventId);
    const nextParams = new URLSearchParams(searchParams);

    if (eventId === 'all') {
      nextParams.delete('eventId');
      nextParams.delete('eventTitle');
    } else {
      nextParams.set('eventId', eventId);
      if (eventTitle) nextParams.set('eventTitle', eventTitle);
    }

    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header & Search */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="text-4xl font-black text-white mb-2 tracking-tight">Gestión de Tickets</h1>
          <p className="text-white/40 font-medium">
            {selectedEventTitle
              ? <>Gestionando accesos de <span className="text-white/70 font-black">{selectedEventTitle}</span>.</>
              : <>Buscá compradores, validá accesos con QR y revisá el estado de cada entrada.</>}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <button
            onClick={() => setIsScannerOpen(true)}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-primary-orange text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 hover:scale-[1.02] active:scale-95 transition-all"
          >
            <QrCode size={20} />
            Escanear QR
          </button>

          <div className="relative group w-full md:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 group-focus-within:text-primary-orange transition-colors" size={18} />
            <input
              type="text"
              placeholder="Buscar por comprador o evento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-surface border border-white/5 rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-white/20 focus:ring-2 focus:ring-primary-orange/50 focus:border-primary-orange transition-all outline-none"
            />
          </div>
        </div>
      </div>

      {/* Event Tabs (Casilleros) */}
      <div className="flex flex-wrap gap-2 mb-8 p-1.5 bg-surface/50 rounded-2xl border border-white/5 backdrop-blur-sm">
        <TabButton
          active={activeEventId === 'all'}
          onClick={() => selectEventFilter('all')}
          label="Todos los Eventos"
          count={tickets.length}
        />
        {eventIds.map(id => (
          <TabButton
            key={id}
            active={activeEventId === id}
            onClick={() => selectEventFilter(id, groupedData.events[id].title)}
            label={groupedData.events[id].title}
            count={groupedData.events[id].tickets.length}
          />
        ))}
      </div>

      {/* Tickets List */}
      <div className="bg-surface rounded-3xl border border-white/5 shadow-2xl overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-50"></div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin mb-4"></div>
            <p className="text-white/50 font-medium animate-pulse text-sm">Actualizando tickets...</p>
          </div>
        ) : currentTickets.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/5 bg-white/5">
                  <th className="px-6 py-4 text-[10px] font-black text-white/40 uppercase tracking-[2px]">Evento</th>
                  <th className="px-6 py-4 text-[10px] font-black text-white/40 uppercase tracking-[2px]">Comprador</th>
                  <th className="px-6 py-4 text-[10px] font-black text-white/40 uppercase tracking-[2px]">Fecha</th>
                  <th className="px-6 py-4 text-[10px] font-black text-white/40 uppercase tracking-[2px]">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {currentTickets.map((ticket) => (
                  <tr key={ticket._id} className="group hover:bg-white/[0.02] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary-orange/10 flex items-center justify-center text-primary-orange">
                          <Calendar size={14} />
                        </div>
                        <span className="text-sm font-bold text-white/80 group-hover:text-white transition-colors">
                          {ticket.event?.title || 'Desconocido'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/40 overflow-hidden">
                          <Users size={14} />
                        </div>
                        <span className="text-sm font-medium text-white/60">
                          {ticket.user?.name || 'Desconocido'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-white/40 font-mono">
                      {ticket.purchaseDate ? new Date(ticket.purchaseDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-right sm:text-left">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                        ticket.valid
                          ? 'bg-green-500/10 text-green-400'
                          : ticket.status === 'expired'
                            ? 'bg-red-500/10 text-red-400/80'
                            : 'bg-white/5 text-white/30'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          ticket.valid 
                            ? 'bg-green-400 animate-pulse' 
                            : ticket.status === 'expired'
                              ? 'bg-red-400/80'
                              : 'bg-white/20'
                        }`}></span>
                        {ticket.valid ? 'Válido' : ticket.status === 'expired' ? 'Vencido' : 'Usado'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-20 text-center">
            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-white/20">
              <Ticket size={40} />
            </div>
            <h3 className="text-white font-bold mb-2">No se encontraron tickets</h3>
            <p className="text-white/30 text-sm max-w-xs mx-auto">Parece que aún no hay ventas para esta selección o tu búsqueda no arrojó resultados.</p>
          </div>
        )}
      </div>
      <ScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScanSuccess={handleScanSuccess}
        slug={slug}
      />
    </div>
  );
};

const TabButton = ({ active, onClick, label, count }) => (
  <button
    onClick={onClick}
    className={`px-5 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-3 whitespace-nowrap ${active
      ? 'bg-white/10 text-white shadow-lg'
      : 'text-white/40 hover:text-white hover:bg-white/5'
      }`}
  >
    {label}
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-black ${active ? 'bg-primary-orange text-white' : 'bg-white/5 text-white/30'}`}>
      {count}
    </span>
  </button>
);

export default AdminTickets;
